<template>
    <div class="h-full flex flex-col space-y-4">
        <div class="flex items-center justify-between space-x-2">
            <div class="flex items-center space-x-3">
                <UButton to="/broadcasts" color="white" icon="i-ph-arrow-left" size="sm" />
                <h1 class="text-2xl font-semibold text-gray-700">Messaging Rates</h1>
            </div>
            <UButton @click="overlays.add = true" color="black" icon="i-ph-plus-bold">
                Add Rate
            </UButton>
        </div>

        <div class="flex-1 bg-white ring-1 ring-black/5 rounded-lg shadow-sm overflow-hidden">
            <UTable
                :rows="rates"
                :columns="columns"
                :loading="loading"
                :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                :empty-state="{ icon: 'i-ph-currency-dollar-duotone', label: 'No rates configured.' }"
                :ui="{ thead: 'sticky top-0 bg-white z-10 shadow-sm', td: { padding: 'py-3' } }"
                class="h-full"
            >
                <template #channel-data="{ row }">
                    <UBadge :color="channelColor(row.channel)" variant="subtle" size="sm">
                        {{ row.channel }}
                    </UBadge>
                </template>
                <template #country_code-data="{ row }">
                    <span :class="!row.country_code ? 'text-gray-400 italic' : ''">
                        {{ row.country_code ?? 'default' }}
                    </span>
                </template>
                <template #unit_cost-data="{ row }">
                    {{ Number(row.unit_cost).toFixed(5) }}
                </template>
                <template #source-data="{ row }">
                    <UBadge :color="row.source === 'manual' ? 'amber' : 'gray'" variant="subtle" size="sm">
                        {{ row.source }}
                    </UBadge>
                </template>
                <template #updated_at-data="{ row }">
                    <span class="text-gray-500 text-sm">{{ formatDate(row.updated_at) }}</span>
                </template>
                <template #actions-data="{ row }">
                    <UButton color="gray" variant="ghost" icon="i-ph-pencil-fill" size="xs" @click="openEdit(row)" />
                </template>
            </UTable>
        </div>

        <UModal v-model="overlays.edit">
            <UCard>
                <template #header>
                    <p class="font-semibold text-gray-700">Edit Rate</p>
                </template>
                <div class="space-y-3" v-if="editingRow">
                    <div class="text-sm text-gray-500 space-y-0.5">
                        <p><span class="font-medium text-gray-700">Channel:</span> {{ editingRow.channel }}</p>
                        <p><span class="font-medium text-gray-700">Country:</span> {{ editingRow.country_code ?? 'default' }}</p>
                        <p v-if="editingRow.category"><span class="font-medium text-gray-700">Category:</span> {{ editingRow.category }}</p>
                    </div>
                    <UFormGroup label="Unit Cost" :error="editError">
                        <UInput v-model="editCost" type="number" step="0.00001" min="0" placeholder="0.00000" />
                    </UFormGroup>
                </div>
                <template #footer>
                    <div class="flex justify-end space-x-2">
                        <UButton color="white" @click="overlays.edit = false">Cancel</UButton>
                        <UButton color="black" :loading="saving" @click="saveEdit">Save</UButton>
                    </div>
                </template>
            </UCard>
        </UModal>

        <UModal v-model="overlays.add">
            <UCard>
                <template #header>
                    <p class="font-semibold text-gray-700">Add Rate</p>
                </template>
                <div class="space-y-3">
                    <UFormGroup label="Channel" required>
                        <USelectMenu v-model="addForm.channel" :options="channelOptions" placeholder="Select channel" />
                    </UFormGroup>
                    <UFormGroup label="Country Code">
                        <UInput v-model="addForm.country_code" placeholder="e.g. LB, US (blank = default)" />
                        <p class="text-xs text-gray-400 mt-1">Leave blank to set a default fallback rate.</p>
                    </UFormGroup>
                    <UFormGroup label="Category">
                        <UInput v-model="addForm.category" placeholder="e.g. marketing" />
                    </UFormGroup>
                    <UFormGroup label="Unit Cost" required :error="addError">
                        <UInput v-model="addForm.unit_cost" type="number" step="0.00001" min="0" placeholder="0.00000" />
                    </UFormGroup>
                    <UFormGroup label="Currency">
                        <UInput v-model="addForm.currency" placeholder="USD" />
                    </UFormGroup>
                </div>
                <template #footer>
                    <div class="flex justify-end space-x-2">
                        <UButton color="white" @click="overlays.add = false">Cancel</UButton>
                        <UButton color="black" :loading="saving" @click="saveAdd">Add</UButton>
                    </div>
                </template>
            </UCard>
        </UModal>
    </div>
</template>

<script setup lang="ts">
import { UserRole } from '~/types/user'

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin] })
useHead({ title: 'Messaging Rates' })

const client = useSupabaseClient()
const toast = useToast()

const loading = ref(false)
const saving = ref(false)
const rates = ref<any[]>([])
const editingRow = ref<any>(null)
const editCost = ref('')
const editError = ref('')
const addError = ref('')

const overlays = reactive({ edit: false, add: false })

const addForm = reactive({
    channel: '',
    country_code: '',
    category: '',
    unit_cost: '',
    currency: 'USD',
})

const columns = [
    { key: 'channel', label: 'Channel' },
    { key: 'country_code', label: 'Country' },
    { key: 'category', label: 'Category' },
    { key: 'unit_cost', label: 'Unit Cost' },
    { key: 'currency', label: 'Currency' },
    { key: 'source', label: 'Source' },
    { key: 'updated_at', label: 'Updated' },
    { key: 'actions', class: 'w-px' },
]

const channelOptions = ['email', 'sms', 'whatsapp', 'push']

function channelColor(channel: string) {
    return { email: 'blue', sms: 'green', whatsapp: 'emerald', push: 'purple' }[channel] ?? 'gray'
}

function formatDate(date: string) {
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

onMounted(fetchData)

async function fetchData() {
    loading.value = true
    rates.value = []

    const { data } = await client.from('messaging_rates')
        .select('*')
        .order('channel')
        .order('country_code', { ascending: true, nullsFirst: true })

    if (data) rates.value = data
    loading.value = false
}

function openEdit(row: any) {
    editingRow.value = row
    editCost.value = String(row.unit_cost)
    editError.value = ''
    overlays.edit = true
}

async function saveEdit() {
    const cost = parseFloat(editCost.value)
    if (isNaN(cost) || cost < 0) {
        editError.value = 'Enter a valid non-negative number.'
        return
    }
    editError.value = ''
    saving.value = true

    const { error } = await client.from('messaging_rates')
        .update({ unit_cost: cost, source: 'manual', updated_at: new Date().toISOString() } as never)
        .eq('id', editingRow.value.id)

    saving.value = false
    if (error) {
        toast.add({ title: 'Failed to update rate', color: 'red' })
        return
    }
    overlays.edit = false
    toast.add({ title: 'Rate updated', color: 'green' })
    fetchData()
}

async function saveAdd() {
    const cost = parseFloat(addForm.unit_cost)
    if (!addForm.channel) { addError.value = 'Channel is required.'; return }
    if (isNaN(cost) || cost < 0) { addError.value = 'Enter a valid non-negative number.'; return }
    addError.value = ''
    saving.value = true

    const payload: any = {
        channel: addForm.channel,
        unit_cost: cost,
        currency: addForm.currency || 'USD',
        source: 'manual',
        updated_at: new Date().toISOString(),
    }
    if (addForm.country_code.trim()) payload.country_code = addForm.country_code.trim().toLowerCase()
    if (addForm.category.trim()) payload.category = addForm.category.trim()

    const { error } = await client.from('messaging_rates').insert(payload as never)

    saving.value = false
    if (error) {
        toast.add({ title: 'Failed to add rate', color: 'red' })
        return
    }
    overlays.add = false
    Object.assign(addForm, { channel: '', country_code: '', category: '', unit_cost: '', currency: 'USD' })
    toast.add({ title: 'Rate added', color: 'green' })
    fetchData()
}
</script>
